import type { PboTableColumn } from '@musinsa/pbo-core'
import type { RequiredCandidate } from '@/utils/requiredCells'

// GI31 일반출고 — .handoff/gi-gi31__20260826-1423 spec.md · schema.after.json 기준.

/**
 * 주문유형 옵션.
 *
 * 데모 목록에는 폐기·테스트·증정·재고이동이 들어 있으나 moms-be `OrderType` 열거형에 없는 값이다.
 * 없는 값으로 조회하면 BE 가 "지원하지 않는 주문유형입니다" 로 400 을 낸다 —
 * 고를 수 있는데 반드시 실패하는 선택지는 두지 않는다. 열거형 그대로 맞춘다.
 */
export const ORDER_TYPE_OPTIONS = [
  { value: '일반', label: '일반' },
  { value: 'PLUS', label: 'PLUS' },
  { value: '사내', label: '사내' },
  { value: '사내(해외)', label: '사내(해외)' },
  { value: '명품', label: '명품' },
  { value: '반품', label: '반품' },
  { value: '해외', label: '해외' },
  { value: '해외(현지)', label: '해외(현지)' },
  { value: '교환', label: '교환' },
  { value: '기타', label: '기타' },
  { value: '미지정', label: '미지정' },
]

/** 배송유형 옵션 — moms-be `TransportType` 열거형. 주문유형과 같은 이유로 열거형에 맞춘다. */
export const TRANSPORT_TYPE_OPTIONS = [
  { value: '택배', label: '택배' },
  { value: '택배-명품', label: '택배-명품' },
  { value: '택배-단포', label: '택배-단포' },
  { value: '택배-현지', label: '택배-현지' },
  { value: '새벽배송', label: '새벽배송' },
  { value: '당일배송', label: '당일배송' },
  { value: '익일배송', label: '익일배송' },
  { value: '용차', label: '용차' },
  { value: '특송', label: '특송' },
]

/**
 * 확정이 허용하는 주문유형·배송유형.
 *
 * BE `OrderType.isOutboundGeneralSupported` · `TransportType.isOutboundGeneralSupported` 와 같은 값이다.
 * 확정 요청 전에 화면에서 걸러 서버 왕복 없이 안내한다(1.0 도 같은 검증을 화면에서 했다).
 */
export const CONFIRMABLE_ORDER_TYPES = ['일반']
export const CONFIRMABLE_TRANSPORT_TYPES = ['택배', '택배-단포', '용차']

/**
 * 표 편집 셀에서 고를 수 있는 유형.
 *
 * 필터는 전체 목록(11종·9종)을 그대로 쓴다 — 조회는 어떤 유형이든 가능하다. 그런데 저장은 다르다.
 * moms-be `ValidateGeneralOutboundService.validateSave` 가 전체 행의 주문유형·배송유형을
 * `isOutboundGeneralSupported` 로 검사해서, 그 밖의 값을 고르면 반드시 400 이 난다.
 * 고를 수 있는데 반드시 실패하는 선택지는 두지 않는다.
 */
export const SAVABLE_ORDER_TYPE_OPTIONS = ORDER_TYPE_OPTIONS.filter((o) => CONFIRMABLE_ORDER_TYPES.includes(o.value))
export const SAVABLE_TRANSPORT_TYPE_OPTIONS = TRANSPORT_TYPE_OPTIONS.filter((o) =>
  CONFIRMABLE_TRANSPORT_TYPES.includes(o.value),
)


/**
 * 엑셀 다운로드 사유코드.
 *
 * 1.0 은 `/api/common/combo` 로 `MOMS_COMBO_INFO` 의 DOWNLOAD_CD 를 받아 썼다. 그 경로는 moms-be-1.0 이라
 * 이번 전환 대상에서 쓸 수 없고, 2.0 `MOMS_COMMON_CODE` 에는 DOWNLOAD_CD 가 없다(dev 실측 0건).
 * 아래 3건은 `MOMS_COMBO_INFO` 실측값이다. 2.0 콤보 API 가 생기면 이 상수를 걷어낸다.
 */
export const DOWNLOAD_REASON_OPTIONS = [
  { value: '내부공유', label: '내부공유' },
  { value: '외부공유', label: '외부공유' },
  { value: '데이터확인', label: '데이터확인' },
]

/** 엑셀 다운로드 상세사유 길이 — 1.0 RemarkPop 검증과 같다. 생성본 모달에는 [확인] 잠금이 없어 배선(`<모달>_ok`)이 이 값으로 막는다. */
export const REASON_DETAIL_MIN_LENGTH = 5
export const REASON_DETAIL_MAX_LENGTH = 200

/** 엑셀 감사로그 MENU_CD — 2.0 화면코드(1.0 GI31). */
export const EXPORT_LOG_MENU_CD = 'GI37'

/** 저장·확정 1회 상한 — BE `MAX_SAVE_SIZE` · `MAX_CONFIRM_SIZE`. */
export const MAX_SAVE_SIZE = 5000
export const MAX_CONFIRM_SIZE = 5000

/**
 * 저장에 반드시 필요한 컬럼 — moms-be `ValidateGeneralOutboundService.missingFields` 와 같은 목록이다.
 *
 * 표의 별표와 저장 전 검사가 **이 한 곳에서 파생된다.** 둘을 따로 적으면 갈라진다 —
 * 별표는 붙는데 검사에서 빠져 사용자가 비운 채 저장하고 BE 400 으로 튕기는 식이다.
 *
 * 목록은 1.0 GI31 필수 컬럼 11종(dev 1.0 그리드 헤더 빨강 = gridsub required, 실측 2026-10-01)이다 — 사용자 결정 "1.0 처럼 막아".
 * 1.0 `CommonUtil.onSaveData` 가 required 컬럼이 빈 행의 저장을 막았다. BE `missingFields` 는 연락처를 둘 중 하나만 보지만
 * 1.0 은 핸드폰·전화번호를 각각 필수로 막았으므로 1.0 을 따른다. 순서는 1.0 헤더 순서(첫 누락 칸을 그 순서로 알린다).
 */
export const REQUIRED_COLUMNS: { key: string; label: string }[] = [
  { key: 'SPR_NM', label: '화주코드' },
  { key: 'ORD_TYPE', label: '주문유형' },
  { key: 'TRS_TYPE', label: '배송유형' },
  { key: 'BARCODE', label: '바코드' },
  { key: 'ORD_QTY', label: '주문수량' },
  { key: 'ORD_NM', label: '주문자명' },
  { key: 'TRS_ADDR', label: '주소' },
  { key: 'TRS_ZIP', label: '우편번호' },
  { key: 'TRS_MOBILE', label: '핸드폰' },
  { key: 'TRS_PHONE', label: '전화번호' },
  { key: 'TRS_NM', label: '수령자' },
]

/** MUSINSAGLOBAL 화주만 추가로 요구하는 컬럼(BE `missingFields` 의 globalMissing). */
export const GLOBAL_SHIPPER_CODE = 'MUSINSAGLOBAL'
export const GLOBAL_REQUIRED_COLUMNS: { key: string; label: string }[] = [
  { key: 'UID', label: 'UID' },
  { key: 'OPT_CD', label: '옵션코드' },
]

/** 화면관리 그리드 설정(LOGISPRD-17254) — 표 id(생성본 columnLayout 키) → gridCd(docs/conventions/grid-code.md). 개인 컬럼 배치 저장도 같은 값을 쓴다 */
export const TABLE_ID = 'genOutTable'
export const GRID_CDS = { [TABLE_ID]: 'GI37_HEAD' } as const

/**
 * BE `ValidateGeneralOutboundService.missingFields` 가 비면 저장을 거부하는 칸 — 화면관리 설정이 필수를 꺼도 화면이 계속 막는다.
 * 연락처(핸드폰·전화번호)는 BE 가 둘 중 하나만 보므로 넣지 않고 설정을 따른다.
 */
export const BE_REQUIRED_KEYS: ReadonlySet<string> = new Set(['BARCODE', 'ORD_QTY', 'ORD_NM', 'TRS_NM', 'TRS_ADDR', 'TRS_ZIP'])

/** 별표가 붙은 컬럼에서 저장 필수 목록을 뽑는다 — 표 별표와 저장 전 검사가 같은 목록을 본다. 순서는 표 컬럼 순서(첫 누락 칸을 그 순서로 알린다) */
export const toRequiredColumns = (columns: RequiredCandidate[]): { key: string; label: string }[] =>
  columns.filter((column) => column.required && typeof column.dataIndex === 'string').map((column) => ({ key: column.dataIndex as string, label: String(column.title) }))

const REQUIRED_KEYS = new Set(REQUIRED_COLUMNS.map((c) => c.key))

/** 표 컬럼. spec.md 의 제목·dataIndex·폭·정렬·렌더를 그대로 옮긴 것이다. */
export const COLUMNS: PboTableColumn[] = [
  { id: 'c-no', title: 'No', dataIndex: '__rownum', width: 60, align: 'center', fixed: 'left', variant: 'index' },
  { id: 'c-sprNm', title: '화주코드', dataIndex: 'SPR_NM', width: 120, fixed: 'left' },
  { id: 'c-ordType', title: '주문유형', dataIndex: 'ORD_TYPE', width: 120, variant: 'select', variantProps: { options: SAVABLE_ORDER_TYPE_OPTIONS } },
  { id: 'c-trsType', title: '배송유형', dataIndex: 'TRS_TYPE', width: 120, variant: 'select', variantProps: { options: SAVABLE_TRANSPORT_TYPE_OPTIONS } },
  { id: 'c-uid', title: 'UID', dataIndex: 'UID', width: 120 },
  { id: 'c-barcode', title: '바코드', dataIndex: 'BARCODE', width: 180 },
  { id: 'c-skuId', title: 'SKUID', dataIndex: 'SKU_ID', width: 150 },
  { id: 'c-gdsCd', title: '상품코드', dataIndex: 'GDS_CD', width: 160, variant: 'input' },
  { id: 'c-gdsNm', title: '상품명', dataIndex: 'GDS_NM', width: 240, variant: 'input' },
  { id: 'c-brdNm', title: '브랜드', dataIndex: 'BRD_NM', width: 180, variant: 'input' },
  { id: 'c-optCd', title: '옵션코드', dataIndex: 'OPT_CD', width: 160 },
  { id: 'c-gdsOpt', title: '옵션명', dataIndex: 'GDS_OPT', width: 120, variant: 'input' },
  { id: 'c-stlNo', title: '스타일넘버', dataIndex: 'STL_NO', width: 160 },
  { id: 'c-gift', title: '사은품', dataIndex: 'GIFT', width: 120, variant: 'input' },
  { id: 'c-ordNo', title: '주문번호', dataIndex: 'ORD_NO', width: 160, variant: 'input' },
  { id: 'c-ordOptNo', title: '주문일련번호', dataIndex: 'ORD_OPT_NO', width: 160, variant: 'input' },
  { id: 'c-ordQty', title: '주문수량', dataIndex: 'ORD_QTY', width: 120, align: 'right', variant: 'input' },
  { id: 'c-ordNm', title: '주문자명', dataIndex: 'ORD_NM', width: 120, variant: 'input' },
  { id: 'c-trsAddr', title: '주소', dataIndex: 'TRS_ADDR', width: 350, variant: 'input' },
  { id: 'c-trsZip', title: '우편번호', dataIndex: 'TRS_ZIP', width: 120, variant: 'input' },
  { id: 'c-trsMsg', title: '출고메시지', dataIndex: 'TRS_MSG', width: 120, variant: 'input' },
  { id: 'c-trsPass', title: '출입방법', dataIndex: 'TRS_PASS', width: 120, variant: 'input' },
  { id: 'c-procNm', title: '특이사항', dataIndex: 'PROC_NM', width: 500, variant: 'input' },
  { id: 'c-trsMobile', title: '핸드폰', dataIndex: 'TRS_MOBILE', width: 150, variant: 'input' },
  { id: 'c-trsPhone', title: '전화번호', dataIndex: 'TRS_PHONE', width: 140, variant: 'input' },
  { id: 'c-trsNm', title: '수령자', dataIndex: 'TRS_NM', width: 120, variant: 'input' },
  { id: 'c-plnDate', title: '출고예정일자', dataIndex: 'PLN_DATE', width: 140, variant: 'datepicker' },
  { id: 'c-insDate', title: '생성일자', dataIndex: 'INS_DATE', width: 120 },
  { id: 'c-insBy', title: '생성자', dataIndex: 'INS_BY', width: 120 },
  { id: 'c-updDate', title: '수정일자', dataIndex: 'UPD_DATE', width: 120 },
  { id: 'c-updBy', title: '수정자', dataIndex: 'UPD_BY', width: 120 },
]

/** 필수 컬럼에 별표를 붙인다 — 목록은 REQUIRED_COLUMNS 한 곳에서 온다. */
COLUMNS.forEach((column) => {
  if (column.dataIndex && REQUIRED_KEYS.has(column.dataIndex)) column.required = true
})

/** 엑셀 업로드·다운로드가 공유하는 컬럼 정의(No 는 파생값이라 제외). 헤더 문구가 업로드 매핑 키다. */
export const EXPORT_COLUMNS = COLUMNS.filter((c) => c.variant !== 'index' && c.dataIndex).map((c) => ({
  key: c.dataIndex as string,
  title: String(c.title ?? c.dataIndex),
}))

/** 엑셀 업로드로 채울 수 있는 컬럼 — 편집 셀 + 식별에 필요한 값. 생성/수정 이력 등 파생 컬럼은 무시한다. */
export const IMPORTABLE_KEYS = new Set([
  'SPR_NM',
  'ORD_TYPE',
  'TRS_TYPE',
  'UID',
  'BARCODE',
  'GDS_CD',
  'GDS_NM',
  'BRD_NM',
  'OPT_CD',
  'GDS_OPT',
  'STL_NO',
  'GIFT',
  'ORD_NO',
  'ORD_OPT_NO',
  'ORD_QTY',
  'ORD_NM',
  'TRS_ADDR',
  'TRS_ZIP',
  'TRS_MSG',
  'TRS_PASS',
  'PROC_NM',
  'TRS_MOBILE',
  'TRS_PHONE',
  'TRS_NM',
  'PLN_DATE',
])

/** [삭제] 문구 — 건수를 받는 함수라 `MESSAGES`(문자열만 담아 행 문구 판정 isRowMessage 에 쓴다)와 따로 둔다. 일반입고 2.0 [삭제] 와 같은 문구. */
export const DELETE_MESSAGES = {
  confirm: (count: number) => `선택한 ${count}건을 삭제하시겠습니까?`,
  done: (count: number) => `${count}건 삭제 완료`,
} as const

export const MESSAGES = {
  shipperRequired: '화주명을 선택해주세요.',
  shipperLoadFailed: '화주 목록을 가져올 수 없습니다. 다시 시도해 주세요.',
  selectRequired: '선택된 행이 없습니다.',
  orderTypeRequired: '주문유형을 선택해주시기 바랍니다.',
  transportTypeRequired: '배송유형을 선택해주시기 바랍니다.',
  orderTypeNotConfirmable: `일반출고 확정은 주문유형 ${CONFIRMABLE_ORDER_TYPES.join('·')} 만 가능합니다.`,
  transportTypeNotConfirmable: `일반출고 확정은 배송유형 ${CONFIRMABLE_TRANSPORT_TYPES.join('·')} 만 가능합니다.`,
  saveSizeExceeded: `1회 저장은 최대 ${MAX_SAVE_SIZE.toLocaleString()}건까지 가능합니다.`,
  confirmSizeExceeded: `1회 확정은 최대 ${MAX_CONFIRM_SIZE.toLocaleString()}건까지 가능합니다.`,
  confirmNotSaved: '저장된 내역이 없어 확정할 수 없습니다. 저장 후 다시 조회해주세요.',
  deleteSizeExceeded: `1회 삭제는 최대 ${MAX_SAVE_SIZE.toLocaleString()}건까지 가능합니다.`,
  deleteCancelled: '삭제를 취소했습니다.',
  excelNoRows: '다운로드 할 데이터가 없습니다. 조회 후 다운로드 해주세요.',
  downloadReasonInvalid: `사유코드를 고르고 상세사유를 ${REASON_DETAIL_MIN_LENGTH}~${REASON_DETAIL_MAX_LENGTH}글자로 입력해주세요.`,
  uploadFileRequired: '업로드할 엑셀 파일을 선택해주세요.',
  uploadXlsxOnly: '엑셀(.xlsx) 파일만 업로드할 수 있습니다.',
  // 저장 상한을 넘는 파일은 어차피 저장되지 않는다 — 표에 올리기 전에 막아 대량 렌더로 탭이 멈추지 않게 한다(LOGISPRD-14800).
  uploadWhileBusy: '저장·확정이 진행 중입니다. 끝난 뒤에 업로드해주세요.',
  uploadNeedsSearch: '화주를 정한 뒤 조회를 먼저 해주세요. 업로드한 행은 조회한 화주로 저장됩니다.',
  // 1.0 progressPollingEpic 이 errorCount 5회에서 띄우던 문구와 같다.
  progressUnavailable: '일시적인 오류로 처리 상태를 확인하지 못했습니다. 잠시 후 조회해 결과를 확인해주세요.',
  orderNoTooLong: '수정이 필요한 주문 번호가 있습니다. 주문 번호를 확인해주세요.',
  phoneInvalid: '전화번호, 핸드폰 번호에 숫자와 -, + 이외의 값은 입력할 수 없습니다.',
  zipInvalid: '우편번호에 숫자와 - 이외의 값은 입력할 수 없습니다.',
  titleNotice: '저장 중인 출고정보는 조회되지 않습니다.',
  // 메뉴 이동·[초기화] 는 표를 비운다 — 저장하지 않은 편집·업로드 행은 사라진다(LOGISPRD-14801, 기타출고와 같은 문구).
  leaveUnsaved: '저장하지 않은 변경 내용이 있습니다. 페이지를 옮기면 사라집니다. 계속할까요?',
  resetUnsaved: '저장하지 않은 변경 내용이 있습니다. 초기화하면 사라집니다. 계속할까요?',
}
